import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"

import { PageHeader } from "@/components/common/page-header"
import { RecordColumn } from "@/components/common/record"
import { ApiNotice } from "@/components/common/api-notice"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { FirmIdentitySection } from "@/features/firms/components/firm-identity-section"
import { FirmYearSection } from "@/features/firms/components/firm-year-section"
import {
  firmRecordInput,
  firmRecordValues,
  validateFirmRecord,
} from "@/features/firms/lib/firm-record-values"
import { useRecordForm } from "@/hooks/use-record-form"
import { firmQueries, useUpdateFirm } from "@/queries/firms"

const formId = "firm-record"

export const Route = createFileRoute(
  "/$organisation/_authenticated/_organisation/firms/$firmId/details/edit/"
)({
  staticData: { attention: "firm.details" },
  component: EditFirmRecordPage,
})

/** The firm's details as one form, with Save in the header; back to the view once saved. */
function EditFirmRecordPage() {
  const { firmId } = Route.useParams()
  const { data: firm } = useSuspenseQuery(firmQueries.detail(firmId))
  const navigate = useNavigate()

  const form = useRecordForm({
    initial: firmRecordValues(firm),
    mutation: useUpdateFirm(firmId),
    toValues: firmRecordValues,
    toInput: firmRecordInput,
    validate: validateFirmRecord,
  })

  return (
    <>
      <PageHeader
        title="Edit firm information"
        actions={
          <>
            <Button
              variant="ghost"
              disabled={form.saving}
              nativeButton={false}
              render={
                <Link
                  from="/$organisation"
                  to="/$organisation/firms/$firmId/details"
                  params={{ firmId }}
                >
                  Cancel
                </Link>
              }
            />
            <Button
              type="submit"
              form={formId}
              disabled={!form.dirty || form.saving}
            >
              {form.saving ? <Spinner /> : null}
              Save
            </Button>
          </>
        }
      />

      <RecordColumn>
        {firm.editNotice ? <ApiNotice notice={firm.editNotice} /> : null}

        <form
          id={formId}
          onSubmit={(event) =>
            void form.submit(event).then((saved) => {
              if (saved) {
                void navigate({
                  from: "/$organisation",
                  to: "/$organisation/firms/$firmId/details",
                  params: { firmId },
                })
              }
            })
          }
          noValidate
          className="flex flex-col gap-5"
        >
          <FirmIdentitySection form={form} />
          <FirmYearSection form={form} />
        </form>
      </RecordColumn>
    </>
  )
}
